/**
 * StationActionPanel - Inspector Panel for Station Actions
 *
 * The "Inspector Pattern" component that handles all station actions.
 * Separates navigation (graph) from action (this panel).
 *
 * Logic mirrors StationCard.tsx to ensure consistent behavior.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Search,
  Mic,
  FileText,
  Cpu,
  Tag,
  Network,
  Box,
  Settings as SettingsIcon,
  Play,
  Loader2,
  RotateCw,
  Lightbulb,
  Check,
  AlertTriangle,
  X,
  BookOpen,
  type LucideIcon,
} from 'lucide-react';
import { Station } from '../../components/StationCard';
import { useApiClient } from '../../hooks/useApiClient';
import { useJourneyId } from '../../contexts/JourneyContext';

// ============================================================================
// TYPES
// ============================================================================

interface Asset {
  id?: string;
  asset_id?: string;
  type: 'dataset' | 'interview' | 'document';
  name: string;
  file_path?: string;
  location?: string;
  uploaded_at?: string;
  created_at?: string;
  health_score?: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
}

interface StationActionPanelProps {
  station: Station;
  groupId?: string;
  journeyAssets: Asset[];
  onRefresh: () => void;
}

// ============================================================================
// STATION METADATA REGISTRY
// ============================================================================
//
// DC-1586 (Epic F): icons are LucideIcon components, not emoji strings. The
// Inspector pane is the demo-critical surface — Kathleen midpoint walkthrough
// can't flinch at toy glyphs. The data-shape `Station.icon?: string` (declared
// on the API row in StationCard.tsx) is left as-is for now; consumers either
// look up by station_id in this registry or fall back to the Box icon.

interface StationMetadata {
  name: string;
  icon: LucideIcon;
  description: string;
  required: boolean;
  estimatedMinutes: number;
}

const STATION_METADATA: Record<string, StationMetadata> = {
  data_quality: {
    name: 'Data Quality Station',
    icon: BarChart3,
    description: 'Statistical quality metrics',
    required: true,
    estimatedMinutes: 5,
  },
  live_checks: {
    name: 'FAIR Assessment Station',
    icon: Search,
    description: 'PID/license validation',
    required: true,
    estimatedMinutes: 3,
  },
  interviews: {
    name: 'Interviews Station',
    icon: Mic,
    description: 'Pain point extraction',
    required: false,
    estimatedMinutes: 2,
  },
  documents: {
    name: 'Data Governance Station',
    icon: FileText,
    description: 'FAIR scoring',
    required: true,
    estimatedMinutes: 10,
  },
  ml_readiness: {
    name: 'ML Readiness Station',
    icon: Cpu,
    description: 'ML suitability assessment',
    required: false,
    estimatedMinutes: 8,
  },
  controlled_vocabularies: {
    name: 'Controlled Vocabularies Station',
    icon: Tag,
    description: 'Ontology validation',
    required: false,
    estimatedMinutes: 4,
  },
  ontology_inference: {
    name: 'Ontology Inference Station',
    icon: Network,
    description: 'ML-powered ontology mapping',
    required: false,
    estimatedMinutes: 1,
  },
};

// ============================================================================
// ASSET ROUTING LOGIC
// ============================================================================

function getExpectedAssets(stationId: string, assets: Asset[]): Asset[] {
  const routingMap: Record<string, Array<'dataset' | 'interview' | 'document'>> = {
    data_quality: ['dataset'],
    live_checks: ['dataset'],
    interviews: ['interview'],
    documents: ['document'],
    ml_readiness: ['dataset'],
    controlled_vocabularies: ['dataset'],
    ontology_inference: ['dataset'],
  };

  const expectedTypes = routingMap[stationId] || [];
  return assets.filter(asset => expectedTypes.includes(asset.type));
}

function navigateToAnalysisPage(
  stationId: string,
  journeyId: string,
  assets: Asset[]
) {
  const routingMap: Record<string, string> = {
    data_quality: 'data-quality',
    live_checks: 'live',
    interviews: 'interview',
    documents: 'docs',
    ml_readiness: 'utils',
    controlled_vocabularies: 'ontology',
    ontology_inference: 'ontology-inference',
  };

  const targetTab = routingMap[stationId];
  if (!targetTab) {
    alert('Analysis page not configured for this station');
    return;
  }

  const assetIds = assets.map(a => a.asset_id || a.id).filter(Boolean).join(',');

  const params = new URLSearchParams({
    journey_id: journeyId,
    station_id: stationId,
    return: 'true',
  });

  if (assetIds) {
    params.set('assets', assetIds);
  }

  // In development, Vite runs on 5173 but legacy pages are served by Express on 3001
  const expressBase = window.location.port === '5173' ? 'http://localhost:3001' : '';
  const analysisBaseUrl = `${expressBase}/index.html`;
  const targetUrl = `${analysisBaseUrl}#/${targetTab}?${params.toString()}`;
  console.log('[StationActionPanel] Navigating to:', targetUrl);
  window.location.href = targetUrl;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StationActionPanel: React.FC<StationActionPanelProps> = ({
  station,
  groupId: groupIdProp,
  journeyAssets,
  onRefresh
}) => {
  const groupId = useJourneyId(groupIdProp);
  const { authFetch } = useApiClient();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  // Get station metadata. When station_id is unknown to the registry, fall
  // back to the generic Box icon — the API-side `station.icon` string is
  // intentionally ignored (Epic F dropped emoji glyphs everywhere).
  const metadata: StationMetadata = STATION_METADATA[station.station_id] || {
    name: station.name || station.station_id,
    icon: Box,
    description: station.description || 'Station',
    required: station.required || false,
    estimatedMinutes: station.time_estimate_minutes || 5,
  };

  // Get expected assets for this station
  const expectedAssets = getExpectedAssets(station.station_id, journeyAssets);

  // Handle station action (Start/View/Retry)
  const handleRunStation = async () => {
    // Special case: Interview station just navigates to existing Interview Analysis page
    if (station.station_id === 'interviews' && station.status !== 'completed') {
      console.log('[StationActionPanel] Interview station - navigating to analysis page');
      navigateToAnalysisPage(station.station_id, groupId, expectedAssets);
      return;
    }

    // Special case: Live Checks (FAIR Assessment) station navigates to Analysis page
    if (station.station_id === 'live_checks' && station.status !== 'completed') {
      console.log('[StationActionPanel] Live Checks station - navigating to analysis page');
      navigateToAnalysisPage(station.station_id, groupId, expectedAssets);
      return;
    }

    // Special case: ML Readiness station navigates to Analysis page
    if (station.station_id === 'ml_readiness' && station.status !== 'completed') {
      console.log('[StationActionPanel] ML Readiness station - navigating to analysis page');
      navigateToAnalysisPage(station.station_id, groupId, expectedAssets);
      return;
    }

    // Handle completed stations - view results
    if (station.status === 'completed') {
      handleViewResults();
      return;
    }

    // Call Station Orchestrator API to execute station
    setIsProcessing(true);
    try {
      console.log('[StationActionPanel] Calling orchestrator API for station:', station.station_id);

      const assetIds = expectedAssets.map(a => a.asset_id || a.id).filter(Boolean);
      console.log('[StationActionPanel] Asset IDs to process:', assetIds);

      const response = await authFetch(`/api/journey/${groupId}/station/${station.station_id}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          asset_ids: assetIds.length > 0 ? assetIds : undefined,
          timeout_ms: 5 * 60 * 1000
        })
      });

      if (!response.ok) {
        // Handle non-JSON error responses (e.g., Azure 504 HTML pages)
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to start station');
        } else {
          // Azure gateway timeout or other non-JSON error
          if (response.status === 504) {
            throw new Error('Request timed out. The server took too long to respond. For large files, this may take several minutes - please try again or use a smaller dataset.');
          }
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      const result = await response.json();
      console.log('[StationActionPanel] Station started:', result);

      // For navigation-based stations (estimated_duration = 0), navigate to analysis page
      if (result.estimated_duration === 0) {
        console.log('[StationActionPanel] Navigation-based station - redirecting to analysis page');
        navigateToAnalysisPage(station.station_id, groupId, expectedAssets);
        return;
      }

      // Refresh journey data to show updated station status
      onRefresh();
    } catch (error: unknown) {
      console.error('[StationActionPanel] Failed to start station:', error);
      alert(`Failed to start station: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle "View Results" - open results page
  const handleViewResults = () => {
    // FAIR (live_checks), Data Quality, and ML Readiness route to in-window journey
    // tabs (PR #27-29 shipped FAIR; DC-1549 adds DQ + ML). User stays in-context.
    if (station.station_id === 'live_checks') {
      navigate(`/journey/${groupId}?tab=fair`);
      return;
    }
    if (station.station_id === 'data_quality') {
      navigate(`/journey/${groupId}?tab=data-quality`);
      return;
    }
    if (station.station_id === 'ml_readiness') {
      navigate(`/journey/${groupId}?tab=ml-readiness`);
      return;
    }

    const jobId = station.job_id || station.results?.job_id;

    if (!jobId) {
      console.error('[StationActionPanel] No job_id found in station');
      alert('Unable to load results: No job ID available');
      return;
    }

    // Ontology Inference results: in-window tab on JourneyNavigator (DC-1569 #3).
    // The OntologyInferenceLegacyWrapper iframes the legacy page so the user
    // keeps journey context, scroll, and FAIRy state. StationResultsTabContent
    // resolves jobId from journey.stations, so the URL only needs ?tab=.
    if (station.station_id === 'ontology_inference') {
      navigate(`/journey/${groupId}?tab=ontology-inference`);
      return;
    }

    // SPA-routable stations (interviews, documents, controlled_vocabularies, …):
    // stay in-window so journey context, scroll, and any FAIRy state are preserved.
    const resultsPath = station.station_id === 'interviews'
      ? `/journey/${groupId}/station/interviews/results?job_id=${jobId}`
      : `/journey/${groupId}/station/${station.station_id}/results?job_id=${jobId}`;

    navigate(resultsPath);
  };

  // Dynamic button renderer based on station status
  const renderActionButton = () => {
    const getButtonLabel = () => {
      if (isProcessing) return 'Processing...';
      switch (station.status) {
        case 'pending': return 'Start Analysis';
        case 'in_progress': return 'Running...';
        case 'completed': return 'View Results';
        case 'failed': return 'Retry Analysis';
        default: return 'Start';
      }
    };

    const getButtonIcon = (): LucideIcon => {
      if (isProcessing) return SettingsIcon;
      switch (station.status) {
        case 'pending': return Play;
        case 'in_progress': return Loader2;
        case 'completed': return BarChart3;
        case 'failed': return RotateCw;
        default: return Play;
      }
    };

    // Render-helper for the button glyph — keeps the JSX below readable. The
    // spinner-small fallback already covers isProcessing; this is the
    // non-loading visual. DC-1586 follow-up: spin the Lucide Loader2 (and
    // the in-processing Settings glyph) so the icon actually reads as
    // "running" — without the animation class the SVG is static.
    const ButtonIcon = getButtonIcon();
    const buttonIconSpinning =
      isProcessing || station.status === 'in_progress';
    const buttonIconNode = (
      <ButtonIcon
        size={16}
        aria-hidden="true"
        className={buttonIconSpinning ? 'spin' : undefined}
        style={{ verticalAlign: 'middle', marginRight: 6 }}
      />
    );

    switch (station.status) {
      case 'pending':
        return (
          <button
            className="btn btn-primary btn-lg full-width"
            onClick={handleRunStation}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner-small" />
                Starting...
              </>
            ) : (
              <>{buttonIconNode}{getButtonLabel()}</>
            )}
          </button>
        );

      case 'in_progress':
        return (
          <button className="btn btn-secondary btn-lg full-width" disabled>
            <span className="pulsing-dot" />
            Analysis Running...
          </button>
        );

      case 'completed':
        return (
          <button
            className="btn btn-success btn-lg full-width"
            onClick={handleViewResults}
          >
            {buttonIconNode}{getButtonLabel()}
          </button>
        );

      case 'failed':
        return (
          <button
            className="btn btn-danger btn-lg full-width"
            onClick={handleRunStation}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner-small" />
                Retrying...
              </>
            ) : (
              <>{buttonIconNode}{getButtonLabel()}</>
            )}
          </button>
        );

      default:
        return null;
    }
  };

  // Get status color for badge
  const getStatusColor = () => {
    switch (station.status) {
      case 'completed': return { bg: '#d1fae5', color: '#065f46' };
      case 'in_progress': return { bg: '#dbeafe', color: '#1e40af' };
      case 'failed': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const statusColor = getStatusColor();

  return (
    <div className="inspector-panel animate-slide-left">
      {/* Header */}
      <div className="inspector-header">
        <div className="inspector-icon">
          {(() => {
            const Icon = metadata.icon;
            return <Icon size={32} aria-hidden="true" />;
          })()}
        </div>
        <div className="inspector-title">
          <h2>{metadata.name}</h2>
          <span
            className="status-badge"
            style={{
              background: statusColor.bg,
              color: statusColor.color,
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {station.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Primary Action Zone */}
      <div className="inspector-actions">
        {renderActionButton()}
        {station.status === 'pending' && metadata.estimatedMinutes && (
          <p className="action-hint">
            Estimated time: ~{metadata.estimatedMinutes} mins
          </p>
        )}
        {station.status === 'in_progress' && (
          <div className="progress-indicator">
            <div className="progress-bar-mini">
              <div
                className="progress-fill"
                style={{ width: `${station.completion_percentage || 50}%` }}
              />
            </div>
            <span className="progress-text">{station.completion_percentage || 50}% complete</span>
          </div>
        )}
      </div>

      <hr className="inspector-divider" />

      {/* Metrics / Details */}
      <div className="inspector-details">
        <h4>Station Metrics</h4>

        <div className="metric-row">
          <span>Progress</span>
          <strong>
            {station.status === 'completed'
              ? '100'
              : (station.completion_percentage || (station.status === 'in_progress' ? 50 : 0))}%
          </strong>
        </div>

        <div className="metric-row">
          <span>Assets Processed</span>
          <strong>
            {station.status === 'completed'
              ? (station.assets_analyzed || station.assets_processed?.length || expectedAssets.length)
              : (station.assets_analyzed || station.assets_processed?.length || 0)}
            {' / '}
            {station.assets_total || expectedAssets.length || 1}
          </strong>
        </div>

        <div className="metric-row">
          <span>Required Station</span>
          <strong>{metadata.required ? 'Yes ★' : 'No'}</strong>
        </div>

        <div className="metric-row">
          <span>Est. Duration</span>
          <strong>~{metadata.estimatedMinutes} min</strong>
        </div>
      </div>

      {/* Educational Summary (DC-989) - shown for completed stations */}
      {station.status === 'completed' && station.educational_output?.headline && (
        <div className="educational-summary" style={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Lightbulb size={18} aria-hidden="true" style={{ color: '#0369a1', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: '#0369a1',
                lineHeight: 1.4
              }}>
                {station.educational_output.headline}
              </p>
              {station.educational_output.readiness_verdict && (
                <span style={{
                  display: 'inline-block',
                  marginTop: '8px',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  background: station.educational_output.readiness_verdict === 'ready' ? '#dcfce7' :
                             station.educational_output.readiness_verdict === 'conditional' ? '#fef3c7' :
                             station.educational_output.readiness_verdict === 'needs_work' ? '#fed7aa' : '#fee2e2',
                  color: station.educational_output.readiness_verdict === 'ready' ? '#166534' :
                         station.educational_output.readiness_verdict === 'conditional' ? '#92400e' :
                         station.educational_output.readiness_verdict === 'needs_work' ? '#9a3412' : '#991b1b'
                }}>
                  {station.educational_output.readiness_verdict.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
          {station.educational_output.key_findings && station.educational_output.key_findings.length > 0 && (
            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #bae6fd' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Key Insights
              </p>
              {station.educational_output.key_findings.slice(0, 2).map((finding, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  marginTop: idx > 0 ? '6px' : 0
                }}>
                  {finding.type === 'strength' ? (
                    <Check size={14} aria-hidden="true" style={{ color: '#16a34a', flexShrink: 0, marginTop: 1 }} />
                  ) : finding.type === 'concern' ? (
                    <AlertTriangle size={14} aria-hidden="true" style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                  ) : (
                    <X size={14} aria-hidden="true" style={{ color: '#dc2626', flexShrink: 0, marginTop: 1 }} />
                  )}
                  <span style={{ fontSize: '12px', color: '#475569', lineHeight: 1.3 }}>
                    {finding.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Description */}
      <div className="description-box">
        <h4>About this Station</h4>
        <p>{metadata.description}</p>
      </div>

      {/* Error Display */}
      {station.error && (
        <div className="error-banner">
          <AlertTriangle size={20} aria-hidden="true" className="error-icon" style={{ color: '#dc2626', flexShrink: 0 }} />
          <div>
            <strong>Error</strong>
            <p>{station.error}</p>
          </div>
        </div>
      )}

      {/* Quick Actions for Completed Stations */}
      {station.status === 'completed' && (
        <div className="quick-actions">
          <button
            className="btn btn-outline btn-sm"
            onClick={handleViewResults}
          >
            <BarChart3 size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Detailed Results
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={handleRunStation}
            disabled={isProcessing}
          >
            <RotateCw size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Re-run Analysis
          </button>
        </div>
      )}

      {/* Contextual Link for Ontology Stations */}
      {(station.station_id === 'ontology_inference' || station.station_id === 'controlled_vocabularies') && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <a
            href="/ontology"
            className="station-contextual-link"
            style={{
              color: '#60a5fa',
              fontSize: '13px',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <BookOpen size={14} aria-hidden="true" />
            Manage Private Ontologies
          </a>
        </div>
      )}
    </div>
  );
};

export default StationActionPanel;
